import React, { useEffect, useState } from "react";
import { Save, Plus, ShieldCheck, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { AdminHeader } from "@/components/AdminHeader";

const FIELDS = ["headline", "year", "date", "newspaper", "snippet", "source", "source_url", "oddScore"];
const inp = "w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-ink focus:border-gold outline-none";
const pick = (h) => Object.fromEntries(FIELDS.map((k) => [k, h[k] == null ? "" : String(h[k])]));
/** Non-empty values are sent as-is; a field the editor blanked is sent as null so the API clears it. */
const body = (form, original) => Object.fromEntries(Object.entries(form)
  .filter(([k, v]) => v !== "" || (original && original[k] !== ""))
  .map(([k, v]) => [k, v === "" ? null : k === "year" || k === "oddScore" ? parseInt(v, 10) : v]));

const Row = ({ h, onSaved }) => {
  const [form, setForm] = useState(pick(h));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(pick(h)); }, [h]);
  const dirty = JSON.stringify(form) !== JSON.stringify(pick(h));
  const patch = async (data, msg) => {
    setSaving(true);
    try {
      await api.patch(`/admin/halloffame/${h.id}`, data);
      onSaved();
      toast.success(msg);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <tr data-testid={`hof-row-${h.id}`} className={`border-b border-slate-100 align-top ${h.active === false ? "opacity-50" : ""}`}>
      <td className="py-3 pr-3">
        <button data-testid={`hof-verify-${h.id}`} onClick={() => patch({ verified: !h.verified }, h.verified ? "Marked unverified" : "Marked verified")} disabled={saving}
          className={`inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[10px] font-bold uppercase tracking-wider ${h.verified ? "bg-ink text-gold" : "bg-white border border-slate-300 text-ink-muted hover:border-gold"}`}>
          <ShieldCheck className="w-3 h-3" /> {h.verified ? "Verified" : "Unverified"}
        </button>
      </td>
      <td className="py-3 pr-3 w-24">
        <span data-testid={`hof-next-${h.id}`} className={`font-mono text-[11px] ${h.next_shown === "Today" || h.next_shown === "Tomorrow" ? "font-bold text-gold" : "text-ink-muted"}`}>{h.next_shown || "—"}</span>
        {!h.year && <p className="mt-1 text-[10px] font-semibold text-[#7a1a1d]">No year → no game</p>}
      </td>
      <td className="py-3 pr-3 w-72">
        <input data-testid={`hof-headline-${h.id}`} value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} className={`${inp} font-semibold`} />
        <p className="mt-1 font-mono text-[10px] text-ink-muted">{h.id}</p>
      </td>
      <td className="py-3 pr-3 w-24"><input data-testid={`hof-year-${h.id}`} value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="Year" className={inp} /></td>
      <td className="py-3 pr-3 w-32"><input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} placeholder="YYYY-MM-DD" className={inp} /></td>
      <td className="py-3 pr-3 w-44"><input value={form.newspaper} onChange={(e) => setForm({ ...form, newspaper: e.target.value })} placeholder="Newspaper" className={inp} /></td>
      <td className="py-3 pr-3"><textarea rows={2} value={form.snippet} onChange={(e) => setForm({ ...form, snippet: e.target.value })} placeholder="Snippet (optional)" className={`${inp} resize-y`} /></td>
      <td className="py-3 pr-3 w-44">
        <input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="Source" className={inp} />
        <input value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} placeholder="https://…" className={`${inp} mt-1`} />
      </td>
      <td className="py-3 pr-3 w-20"><input value={form.oddScore} onChange={(e) => setForm({ ...form, oddScore: e.target.value })} className={inp} /></td>
      <td className="py-3 whitespace-nowrap">
        <button data-testid={`hof-save-${h.id}`} onClick={() => patch(body(form, pick(h)), "Headline saved")} disabled={!dirty || saving} className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-ink text-white font-mono text-xs disabled:opacity-30">
          <Save className="w-3.5 h-3.5" /> Save
        </button>
        <button data-testid={`hof-active-${h.id}`} onClick={() => patch({ active: h.active === false }, h.active === false ? "Back in rotation" : "Removed from rotation")} disabled={saving} title={h.active === false ? "Put back in rotation" : "Hide from rotation"}
          className="ml-1 inline-flex items-center gap-1 px-2.5 h-8 rounded-full border border-slate-200 text-ink-muted font-mono text-xs hover:border-gold">
          <EyeOff className="w-3.5 h-3.5" /> {h.active === false ? "Hidden" : "Hide"}
        </button>
      </td>
    </tr>
  );
};

const AddForm = ({ onAdded }) => {
  const blank = { id: "", headline: "", year: "", date: "", newspaper: "", snippet: "", source: "Newspapers.com", source_url: "", oddScore: 85, verified: true };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const id = form.id || form.headline.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 60);
      await api.post("/admin/halloffame", { ...body(form), id, verified: form.verified });
      toast.success("Headline added to the Hall of Fame");
      setForm(blank);
      onAdded();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add headline");
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="hof-add-form" className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Add a verified headline</p>
      <p className="mt-1 text-sm text-ink-muted">Only add headlines you have personally checked against a clipping. Paste the clipping URL as the source link — nothing is ever scraped.</p>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-6 gap-3">
        <input data-testid="hof-add-headline" required value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="Headline exactly as printed" className={`${inp} md:col-span-3 font-semibold`} />
        <input data-testid="hof-add-year" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="Year" className={inp} />
        <input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} placeholder="YYYY-MM-DD" className={inp} />
        <input value={form.newspaper} onChange={(e) => setForm({ ...form, newspaper: e.target.value })} placeholder="Newspaper (City)" className={inp} />
        <textarea rows={2} value={form.snippet} onChange={(e) => setForm({ ...form, snippet: e.target.value })} placeholder="Snippet / first lines (optional)" className={`${inp} md:col-span-3 resize-y`} />
        <input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="Source" className={inp} />
        <input data-testid="hof-add-source-url" value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} placeholder="Clipping URL" className={`${inp} md:col-span-2`} />
        <input value={form.oddScore} onChange={(e) => setForm({ ...form, oddScore: e.target.value })} placeholder="Odd score 0-100" className={inp} />
        <label className="inline-flex items-center gap-2 text-sm text-ink"><input type="checkbox" data-testid="hof-add-verified" checked={form.verified} onChange={(e) => setForm({ ...form, verified: e.target.checked })} /> I have verified this clipping</label>
        <button data-testid="hof-add-submit" type="submit" disabled={saving} className="md:col-span-2 inline-flex items-center justify-center gap-2 h-10 rounded-full bg-ink text-white text-sm font-semibold hover:bg-gold hover:text-ink disabled:opacity-60 transition-colors">
          <Plus className="w-4 h-4" /> {saving ? "Adding…" : "Add to Hall of Fame"}
        </button>
      </div>
    </form>
  );
};

export default function AdminHallOfFame() {
  const [data, setData] = useState({ items: [], total: 0, verified: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const load = () => api.get("/admin/halloffame").then((r) => setData(r.data)).catch(() => toast.error("Could not load the Hall of Fame")).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const items = data.items.filter((h) => (filter === "verified" ? h.verified : filter === "unverified" ? !h.verified : filter === "noyear" ? !h.year : true));

  return (
    <div className="min-h-screen bg-paper">
      <AdminHeader />
      <main className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-halloffame-page">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold">OddlyTrue News · Hall of Fame</p>
        <h1 className="mt-1 text-3xl font-bold text-ink">Strange-but-true vintage headlines</h1>
        <p className="mt-2 text-sm text-ink-muted">One headline rotates onto the homepage each day (sorted by id, day-of-year). <span data-testid="hof-counts" className="font-semibold text-ink">{data.verified} verified of {data.total}</span>. Unverified headlines show a "Classic headline" badge until you confirm the clipping. Saving clears today's cache instantly.</p>
        {data.missing_year > 0 && (
          <p data-testid="hof-missing-year-note" className="mt-2 inline-flex items-center gap-2 rounded-xl border border-gold/50 bg-gold/10 px-3 py-2 text-sm text-ink">
            <span className="font-semibold">{data.missing_year} headline{data.missing_year === 1 ? "" : "s"} without a year.</span> The Guess-the-Year game only appears on days whose headline has a year — add one in the Year column (the "Next shown" column tells you which to fix first).
          </p>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-2">
          {[["all", `All ${data.total}`], ["verified", `Verified ${data.verified}`], ["unverified", `Unverified ${data.total - data.verified}`], ["noyear", `Missing year ${data.missing_year || 0}`]].map(([k, label]) => (
            <button key={k} data-testid={`hof-filter-${k}`} onClick={() => setFilter(k)} className={`px-3 h-8 rounded-full font-mono text-xs ${filter === k ? "bg-ink text-white" : "bg-white border border-slate-200 text-ink-muted"}`}>{label}</button>
          ))}
        </div>

        <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white px-4">
          <table className="w-full text-left min-w-[1100px]">
            <thead>
              <tr className="font-mono text-[10px] uppercase tracking-widest text-ink-muted border-b border-slate-200">
                <th className="py-3 pr-3">Status</th><th className="py-3 pr-3">Next shown</th><th className="py-3 pr-3">Headline</th><th className="py-3 pr-3">Year</th><th className="py-3 pr-3">Date</th><th className="py-3 pr-3">Newspaper</th><th className="py-3 pr-3">Snippet</th><th className="py-3 pr-3">Source</th><th className="py-3 pr-3">Odd</th><th className="py-3" />
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={10} className="py-10 text-center text-sm text-ink-muted">Loading…</td></tr>
                : items.length === 0 ? <tr><td colSpan={10} className="py-10 text-center text-sm text-ink-muted" data-testid="hof-empty">No headlines match.</td></tr>
                : items.map((h) => <Row key={h.id} h={h} onSaved={load} />)}
            </tbody>
          </table>
        </div>

        <AddForm onAdded={load} />
      </main>
    </div>
  );
}
